import { useState, useEffect } from 'react'
import { Search, Bell, Globe, Plus, MapPin } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Sidebar from '../../components/layout/Sidebar'
import Button from '../../components/common/Button'
import { storageService } from '../../services'
import { API_BASE_URL, API_ENDPOINTS } from '../../constants'

interface FarmerProfile {
  firstName: string
  lastName: string
}

interface Farm {
  id: string
  name: string
  farmCode: string
  address?: string
  subdistrict?: string
  district?: string
  province?: string
  postalCode?: string
  description?: string
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED'
  landArea?: number
  latitude?: number
  longitude?: number
  createdAt: string
  _count?: {
    cows: number
  }
}

function FarmListPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [farmerProfile, setFarmerProfile] = useState<FarmerProfile | null>(null)
  const [farms, setFarms] = useState<Farm[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const token = storageService.getAuthToken()

      // Fetch farmer profile
      const profileResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMERS.PROFILE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (profileResponse.ok) {
        const profileData = await profileResponse.json()
        setFarmerProfile(profileData)
      }

      // Fetch farms
      const farmsResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMS.BASE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (farmsResponse.ok) {
        const farmsData = await farmsResponse.json()
        setFarms(farmsData)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredFarms = farms.filter(farm =>
    farm.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar (Top Navbar) */}
      <Sidebar />

      {/* Main Content */}
      <div className="pt-16 lg:pt-20">
        {/* Header - ตาม Figma (Hidden on mobile) */}
        <div className="hidden lg:block bg-white border-b border-border-light px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Left Section */}
            <div className="flex items-center gap-8">
              {/* เกษตรกร Section */}
              <div>
                <h2 className="text-lg font-semibold text-text-primary">เกษตรกร</h2>
                <p className="text-sm text-text-secondary">
                  {isLoading ? 'กำลังโหลด...' : farmerProfile ? `สวัสดี, คุณ${farmerProfile.firstName} ${farmerProfile.lastName}` : 'สวัสดี'}
                </p>
              </div>

              {/* Divider */}
              <div className="h-12 w-px bg-border-light" />

              {/* หน้าฟาร์ม Section */}
              <div>
                <h2 className="text-lg font-semibold text-text-primary">ข้อมูลฟาร์ม</h2>
                <p className="text-sm text-primary-800">
                  {isLoading ? 'กำลังโหลด...' : `จำนวนฟาร์มทั้งหมด ${farms.length} ฟาร์ม`}
                </p>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                <input
                  type="text"
                  placeholder="ค้นหา..."
                  className="pl-10 pr-4 py-2 w-64 border border-border-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent"
                />
              </div>

              {/* Icons */}
              <button className="p-2 hover:bg-background-tertiary rounded-lg transition-colors">
                <Bell className="w-5 h-5 text-text-secondary" />
              </button>
              <button className="p-2 hover:bg-background-tertiary rounded-lg transition-colors flex items-center gap-2">
                <Globe className="w-5 h-5 text-text-secondary" />
                <span className="text-sm text-primary-800 font-medium">ภาษาไทย</span>
              </button>
              <button className="p-2 hover:bg-background-tertiary rounded-lg transition-colors">
                <div className="w-8 h-8 bg-primary-800 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  ส
                </div>
              </button>
            </div>
          </div>

          {/* Date */}
          <div className="mt-2 text-right">
            <p className="text-xs text-text-tertiary">วันจันทร์ที่, 16 ตุลาคม 2567 13:00น.</p>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 lg:p-6 lg:p-8 bg-background-secondary min-h-screen">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-1">
              <h1 className="text-xl lg:text-2xl font-semibold text-text-primary whitespace-nowrap">ฟาร์มของฉัน</h1>
              <div className="relative flex-1 sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                <input
                  type="text"
                  placeholder="ค้นหา..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-border-medium rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent"
                />
              </div>
            </div>

            {/* Add Farm Button - ตาม Figma */}
            <Button
              variant="primary"
              size="md"
              className="flex items-center justify-center gap-2 w-full lg:w-auto"
              onClick={() => navigate('/farms/new')}
            >
              <Plus className="w-4 h-4" />
              เพิ่มฟาร์ม
            </Button>
          </div>

          {/* Farm Cards Grid - ตาม Figma */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-text-secondary">กำลังโหลดข้อมูล...</p>
              </div>
            ) : filteredFarms.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-text-secondary">
                  {searchQuery ? 'ไม่พบฟาร์มที่ค้นหา' : 'ยังไม่มีฟาร์ม กรุณาเพิ่มฟาร์มใหม่'}
                </p>
              </div>
            ) : (
              filteredFarms.map((farm) => (
              <div
                key={farm.id}
                className="bg-white rounded-lg p-4 lg:p-6 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 hover:shadow-md transition-shadow cursor-pointer border border-border-light"
                onClick={() => navigate(`/farms/${farm.id}`)}
              >
                {/* Farm Image */}
                <div className="w-full lg:w-72 h-48 lg:h-40 rounded-lg overflow-hidden flex-shrink-0 bg-background-tertiary">
                  <img
                    src="https://images.unsplash.com/photo-1560493676-04071c5f467b?w=800&q=80"
                    alt={farm.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Farm Info */}
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg lg:text-xl font-semibold text-primary-800 mb-2 truncate">
                    {farm.name}
                  </h2>
                  <p className="text-sm text-text-secondary mb-3">
                    เจ้าของฟาร์ม: <span className="text-text-primary">
                      {farmerProfile ? `${farmerProfile.firstName} ${farmerProfile.lastName}` : 'ไม่ระบุ'}
                    </span>
                  </p>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-text-secondary">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary-800 flex-shrink-0" />
                      <span className="truncate">
                        {[farm.district, farm.province].filter(Boolean).join(', ') || 'ไม่ระบุตำแหน่ง'}
                      </span>
                    </div>
                    {farm.landArea && (
                      <div className="flex items-center gap-2">
                        <span>พื้นที่: {farm.landArea} ไร่</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span>จำนวนโค: {farm._count?.cows || 0} ตัว</span>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="flex flex-row lg:flex-col items-center lg:items-end gap-2 pt-3 lg:pt-0 border-t lg:border-t-0 border-border-light">
                  <div className="text-sm text-text-secondary">สถานะฟาร์ม :</div>
                  <div
                    className={`px-3 lg:px-4 py-1.5 rounded-lg text-xs lg:text-sm font-medium ${
                      farm.status === 'ACTIVE'
                        ? 'bg-success-light text-success'
                        : farm.status === 'INACTIVE'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-orange-100 text-orange-600'
                    }`}
                  >
                    {farm.status === 'ACTIVE' ? 'ใช้งาน' : farm.status === 'INACTIVE' ? 'ไม่ใช้งาน' : 'เก็บถาวร'}
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FarmListPage
